import { readingDiagnostic } from "./telemetry";
type QuestionAudioInput = {
  text: string;
  question: string;
  options: string[];
  language: "he" | "en";
};

type Details = Record<string, number | boolean>;
type Recording = { pcm: Uint8Array; attempt: number };

export type PreparedQuestionAudio = {
  recording: Promise<Recording>;
  cancel: () => void;
};

// This checks words, not accent, vowel accuracy or stress: transcription omits niqqud.
export function sameSpokenWords(actual: string, expected: string) {
  const normalize = (text: string) => text.normalize("NFD").replace(/\p{M}/gu, "")
    .toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").trim();
  return normalize(actual) === normalize(expected);
}

class AudioUnavailable extends Error {
  constructor(readonly reason: string, readonly details: Details = {}) {
    super(reason);
  }
}

const words = (text: string) => text.split(/\s+/u).filter(Boolean).length;

// Fetches a token, streams the whole reading from Gemini and checks it before anything can be
// heard. It touches no audio output, so it can start when a question appears and the tap plays
// the finished recording at once.
export function prepareQuestionAudio(input: QuestionAudioInput, request: typeof fetch = fetch): PreparedQuestionAudio {
  const controller = new AbortController();
  const startedAt = Date.now();
  let socket: WebSocket | undefined;
  let attempts = 0;
  let settled = false;
  let timer: ReturnType<typeof setTimeout>;
  let deadline: ReturnType<typeof setTimeout>;
  let resolveRecording!: (recording: Recording) => void;
  let rejectRecording!: (error: AudioUnavailable) => void;
  const recording = new Promise<Recording>((resolve, reject) => {
    resolveRecording = resolve;
    rejectRecording = reject;
  });
  // A question the child never plays must not surface an unhandled rejection.
  recording.catch(() => {});

  function closeSocket() {
    if (!socket) return;
    socket.onopen = socket.onmessage = socket.onerror = socket.onclose = null;
    socket.close();
  }

  function settle() {
    settled = true;
    clearTimeout(timer);
    clearTimeout(deadline);
  }

  function fail(reason: string, details: Details = {}) {
    if (settled) return;
    settle();
    controller.abort();
    closeSocket();
    rejectRecording(new AudioUnavailable(reason, { ...details, attempt: attempts }));
  }

  // Gemini sometimes answers with a text-only turn: the full transcript arrives within seconds with
  // no PCM. That turn is over quickly, so one fresh session fits inside the silence timeout below.
  function retry(reason: string) {
    readingDiagnostic("question_audio_retry", { reason, elapsedMs: Date.now() - startedAt, attempt: attempts });
    closeSocket();
    void connect().catch(() => fail("preparation_failed"));
  }

  // Fail promptly on silence, but let a progressing recording finish before validation.
  timer = setTimeout(() => fail("idle_timeout"), 10_000);
  deadline = setTimeout(() => fail("generation_timeout"), 30_000);

  // One Gemini session: a fresh token, then the whole reading buffered and checked.
  async function connect() {
    if (settled) return;
    attempts++;
    const response = await request("/api/reading/question-audio", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
      signal: controller.signal,
    });
    if (settled) return;
    if (!response.ok) { fail(`token_http_${response.status}`); return; }
    const { token, model } = await response.json();
    if (settled) return;
    const session = new WebSocket(`wss://generativelanguage.googleapis.com/ws/google.ai.generativelanguage.v1beta.GenerativeService.BidiGenerateContentConstrained?access_token=${encodeURIComponent(token)}`);
    socket = session;
    session.binaryType = "arraybuffer";
    const chunks: Uint8Array[] = [];
    let transcript = "";
    let byteLength = 0;
    session.onopen = () => {
      if (!settled) session.send(JSON.stringify({ setup: { model } }));
    };
    session.onerror = () => fail("socket_error");
    session.onclose = (event) => fail(`socket_closed_${event.code}`);
    session.onmessage = (event) => {
      if (settled || session !== socket) return;
      try {
        const message = JSON.parse(typeof event.data === "string" ? event.data : new TextDecoder().decode(event.data));
        if (message.error || message.goAway) { fail(message.error ? "gemini_error" : "gemini_go_away"); return; }
        if (message.setupComplete) {
          // Request narration through realtimeInput after setup. Asking in
          // English to read "aloud" avoids the text-only turns the earlier Hebrew command produced
          // for questions quoting the text twice; see docs/question-audio-fallback.md.
          session.send(JSON.stringify({ realtimeInput: {
            text: `Read the supplied question and options aloud now, in ${input.language === "he" ? "Israeli Hebrew" : "English"}.`,
          } }));
        }
        const content = message.serverContent;
        if (!content) return;
        if (content.interrupted) { fail("interrupted"); return; }
        transcript += content.outputTranscription?.text || "";
        for (const part of content.modelTurn?.parts || []) {
          if (!part.inlineData) continue;
          if (!/^audio\/pcm;rate=24000$/.test(part.inlineData.mimeType)) { fail("invalid_audio"); return; }
          const chunk = Uint8Array.from(atob(part.inlineData.data), (char) => char.charCodeAt(0));
          byteLength += chunk.length;
          if (byteLength > 5_760_000) { fail("audio_too_long"); return; }
          chunks.push(chunk);
          if (chunk.length) {
            clearTimeout(timer);
            timer = setTimeout(() => fail("idle_timeout"), 10_000);
          }
        }
        if (!content.generationComplete && !content.turnComplete) return;
        if (!byteLength) {
          if (attempts < 2) retry("empty_audio");
          else fail("empty_audio");
          return;
        }
        if (byteLength % 2) { fail("invalid_audio"); return; }
        // The whole reading is checked here so extra answers or hints are rejected before any playback.
        const expected = [input.question, ...input.options].join(" ");
        if (!sameSpokenWords(transcript, expected)) {
          fail("transcript_mismatch", { expectedWords: words(expected), spokenWords: words(transcript), latin: /[A-Za-z]/.test(expected) });
          return;
        }
        settle();
        closeSocket();
        const pcm = new Uint8Array(byteLength);
        let offset = 0;
        for (const chunk of chunks) { pcm.set(chunk, offset); offset += chunk.length; }
        resolveRecording({ pcm, attempt: attempts });
      } catch { fail("invalid_response"); }
    };
  }
  void connect().catch(() => fail("preparation_failed"));
  return { recording, cancel: () => fail("cancelled") };
}

export function playQuestionAudio(
  input: QuestionAudioInput,
  fallback: (onEnd: (failed?: boolean) => void) => void | (() => void),
  onEnd: (failed?: boolean) => void,
  onPlaying: () => void = () => {},
  prepared?: PreparedQuestionAudio,
  request: typeof fetch = fetch,
) {
  const startedAt = Date.now();
  let active = true;
  let usingFallback = false;
  let stopFallback: void | (() => void);
  let context: AudioContext | undefined;
  // A preparation started by this tap; a preload passed in belongs to the question, not the tap.
  let own: PreparedQuestionAudio | undefined;

  function closeContext() {
    if (context && context.state !== "closed") void context.close().catch(() => {});
  }

  function stop(failed = false) {
    if (!active) return;
    active = false;
    if (failed) readingDiagnostic("question_audio_failed", { elapsedMs: Date.now() - startedAt });
    own?.cancel();
    closeContext();
    stopFallback?.();
    window.speechSynthesis?.cancel();
    onEnd(failed);
  }

  function useFallback(reason: string, details: Details = {}) {
    if (!active || usingFallback) return;
    usingFallback = true;
    // Never log question text, transcripts, audio, or the ephemeral token: only codes and counts.
    readingDiagnostic("question_audio_fallback", { reason, elapsedMs: Date.now() - startedAt, ...details });
    closeContext();
    try { stopFallback = fallback((failed) => stop(failed)); } catch { stop(true); return; }
    if (active) onPlaying();
  }

  // The preloaded recording when there is one. A preload that failed before the tap gets one
  // fresh try now, since the child is waiting either way.
  async function record() {
    if (prepared) {
      try {
        return { recording: await prepared.recording, preloaded: true };
      } catch {
        if (!active) throw new AudioUnavailable("stopped");
      }
    }
    own = prepareQuestionAudio(input, request);
    return { recording: await own.recording, preloaded: false };
  }

  async function play() {
    // Resume during the click gesture so mobile browsers allow later playback.
    context = new AudioContext();
    await context.resume();
    if (!active) return;
    const { recording: { pcm, attempt }, preloaded } = await record();
    if (!active || usingFallback) return;
    const view = new DataView(pcm.buffer, pcm.byteOffset, pcm.byteLength);
    const buffer = context.createBuffer(1, pcm.byteLength / 2, 24_000);
    const samples = buffer.getChannelData(0);
    for (let i = 0; i < samples.length; i++) samples[i] = view.getInt16(i * 2, true) / 32768;
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.connect(context.destination);
    source.onended = () => { if (!usingFallback) stop(); };
    source.start();
    onPlaying();
    // A suspended context plays silence, so report the state alongside the successful path.
    readingDiagnostic("question_audio_played", { elapsedMs: Date.now() - startedAt, attempt, preloaded, contextState: context.state, seconds: Math.round(pcm.byteLength / 48_000) });
  }
  void play().catch((error: unknown) => {
    if (!active) return;
    if (error instanceof AudioUnavailable) useFallback(error.reason, error.details);
    else useFallback("playback_failed");
  });
  return () => stop();
}
